import React, { useMemo, useState } from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { IoRocketOutline, IoWarningOutline } from 'react-icons/io5';
import classNames from 'styles/utils/classNames';
import { formatCalendarDay, formatMarketCap } from 'screens/Calendar';
import { TickerType } from '../../types';

interface SplitItem {
  symbol: string;
  date: string;
  splitRatio?: string;
  splitType?: string;
  sentiment?: string;
  marketCap?: number;
}

interface SplitsCardProps {
  item: SplitItem;
  isBookmarked: boolean;
  onBookmark: (symbol: string) => void;
  tickerData: TickerType[];
}

const SENTIMENT_CONFIG = {
  Bullish: {
    colorClass: 'text-green-500',
    IconComponent: IoRocketOutline,
  },
  Bearish: {
    colorClass: 'text-yellow-500',
    IconComponent: IoWarningOutline,
  },
} as const;

export const SplitsCard: React.FC<SplitsCardProps> = ({
  item,
  isBookmarked,
  onBookmark,
  tickerData,
}) => {
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  const foundTicker = useMemo(
    () => tickerData.find((ticker) => ticker.t === item.symbol),
    [tickerData, item.symbol],
  );

  const formattedDate = useMemo(
    () => formatCalendarDay(item.date, { month: 'short', day: 'numeric', year: 'numeric' }),
    [item.date],
  );

  const sentimentConfig = useMemo(() => {
    if (!item.sentiment) return null;

    const sentimentKey = Object.keys(SENTIMENT_CONFIG).find((key) =>
      item.sentiment?.includes(key),
    ) as keyof typeof SENTIMENT_CONFIG;

    return sentimentKey ? SENTIMENT_CONFIG[sentimentKey] : null;
  }, [item.sentiment]);

  const handleBookmarkClick = () => onBookmark(item.symbol);

  return (
    <div className="rounded-lg border border-palette-border bg-palette-black-2 p-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-start gap-2">
          <button
            onClick={handleBookmarkClick}
            className={classNames(
              'group p-1 transition-all duration-200',
              isBookmarked ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400',
            )}
            aria-label={`${isBookmarked ? 'Remove' : 'Add'} ${item.symbol} bookmark`}
          >
            {isBookmarked ? (
              <AiFillStar className="h-4 w-4" />
            ) : (
              <>
                <AiOutlineStar className="h-4 w-4 group-hover:hidden" />
                <AiFillStar className="hidden h-4 w-4 group-hover:block" />
              </>
            )}
          </button>
          {imageStatus === 'error' ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-xs font-semibold text-gray-600">
              {item.symbol?.[0]?.toUpperCase() || '•'}
            </div>
          ) : (
            <img
              src={`https://images.financialmodelingprep.com/symbol/${item.symbol}.png`}
              alt={`${item.symbol} logo`}
              className={classNames(
                'h-8 w-8 rounded-lg transition-opacity',
                imageStatus === 'loading' && 'opacity-50',
              )}
              loading="lazy"
              onLoad={() => setImageStatus('loaded')}
              onError={() => setImageStatus('error')}
            />
          )}
          <div>
            <div className="text-sm font-semibold text-white">{item.symbol}</div>
            {foundTicker?.n && <div className="text-xs text-gray-400">{foundTicker.n}</div>}
            {item.marketCap && (
              <div className="text-xs text-gray-400">
                {formatMarketCap(item.marketCap)} Market Cap
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400">Split Date</div>
          <div className="text-xs text-white">{formattedDate}</div>
          <div className="text-xs text-gray-400">{item.splitRatio || '--'}</div>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="block text-xs text-gray-400">Split Type</span>
            <span className="text-white">{item.splitType || '--'}</span>
          </div>

          <div className="text-right">
            <span className="block text-xs text-gray-400">Sentiment</span>
            <div className="flex items-center justify-end gap-2 text-xs">
              {sentimentConfig ? (
                <>
                  <sentimentConfig.IconComponent size={16} className={sentimentConfig.colorClass} />
                  <span className={sentimentConfig.colorClass}>{item.sentiment}</span>
                </>
              ) : (
                <span className="text-gray-400">--</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
